/**
 * Calculates the visual layout for timed events in a day column (Timeline).
 * Groups overlapping events into "islands" and assigns lanes/columns to avoid overlap.
 */
export const getTimedLayout = (events, activeInteraction = null) => {
    if (events.length === 0) return [];

    const stripSeconds = (value) => {
        const copy = new Date(value);
        copy.setSeconds(0, 0);
        return copy;
    };

    const getEventOrder = (event) => {
        const value = Number(event?.stack_order);
        return Number.isFinite(value) ? value : 0;
    };

    let dragInfo = null;
    if (activeInteraction && activeInteraction.moved && (activeInteraction.type === 'move' || activeInteraction.type === 'move-allday')) {
        const dragId = activeInteraction.assignment?.id;
        const dragItem = events.find(e => String(e.id) === String(dragId) || String(e.id) === `ghost-${String(dragId)}`);
        if (dragItem) {
            const dragStart = activeInteraction.currentS || new Date(dragItem.start_datetime);
            const dragEnd = activeInteraction.currentE || new Date(dragItem.end_datetime);
            dragInfo = {
                id: dragId,
                start: dragStart,
                end: dragEnd,
                lane: Number.isFinite(Number(activeInteraction.targetLane)) ? Number(activeInteraction.targetLane) : null
            };
        }
    }

    const sorted = [...events].sort((a, b) => {
        const isADrag = dragInfo && (String(a.id) === String(dragInfo.id) || String(a.id) === `ghost-${String(dragInfo.id)}`);
        const isBDrag = dragInfo && (String(b.id) === String(dragInfo.id) || String(b.id) === `ghost-${String(dragInfo.id)}`);
        const startA = isADrag ? dragInfo.start : new Date(a.start_datetime);
        const startB = isBDrag ? dragInfo.start : new Date(b.start_datetime);
        if (startA.getTime() !== startB.getTime()) return startA - startB;

        const orderA = isADrag ? (dragInfo.lane ?? getEventOrder(a)) : getEventOrder(a);
        const orderB = isBDrag ? (dragInfo.lane ?? getEventOrder(b)) : getEventOrder(b);
        if (orderA !== orderB) return orderA - orderB;

        return String(a.id).localeCompare(String(b.id));
    });

    const islands = [];
    let currentIsland = [];
    let currentIslandEnd = null;

    sorted.forEach((event) => {
        const isADrag = dragInfo && (String(event.id) === String(dragInfo.id) || String(event.id) === `ghost-${String(dragInfo.id)}`);
        const s = isADrag ? dragInfo.start : new Date(event.start_datetime);
        const e = isADrag ? dragInfo.end : new Date(event.end_datetime);
        const cleanS = stripSeconds(s);
        const cleanE = stripSeconds(e);

        if (currentIsland.length === 0 || cleanS < currentIslandEnd) {
            currentIsland.push(event);
            if (!currentIslandEnd || cleanE > currentIslandEnd) currentIslandEnd = cleanE;
        } else {
            islands.push(currentIsland);
            currentIsland = [event];
            currentIslandEnd = cleanE;
        }
    });

    if (currentIsland.length > 0) {
        islands.push(currentIsland);
    }

    const allLayouts = [];

    islands.forEach(island => {
        const orderedIsland = [...island].sort((a, b) => {
            const isADrag = dragInfo && (String(a.id) === String(dragInfo.id) || String(a.id) === `ghost-${String(dragInfo.id)}`);
            const isBDrag = dragInfo && (String(b.id) === String(dragInfo.id) || String(b.id) === `ghost-${String(dragInfo.id)}`);
            if (isADrag && !isBDrag) return -1;
            if (isBDrag && !isADrag) return 1;

            const orderA = getEventOrder(a);
            const orderB = getEventOrder(b);
            if (orderA !== orderB) return orderA - orderB;

            const startA = new Date(a.start_datetime);
            const startB = new Date(b.start_datetime);
            if (startA.getTime() !== startB.getTime()) return startA - startB;

            return String(a.id).localeCompare(String(b.id));
        });

        if (dragInfo) {
            const dragIndex = orderedIsland.findIndex(event => String(event.id) === String(dragInfo.id) || String(event.id) === `ghost-${String(dragInfo.id)}`);
            if (dragIndex !== -1) {
                const [dragged] = orderedIsland.splice(dragIndex, 1);
                const targetLane = Number.isFinite(dragInfo.lane) ? Math.max(0, Math.min(orderedIsland.length, dragInfo.lane)) : dragIndex;
                orderedIsland.splice(targetLane, 0, dragged);
            }
        }

        const columns = [];
        const islandProcessed = [];

        orderedIsland.forEach(event => {
            const isDragging = dragInfo && (String(event.id) === String(dragInfo.id) || String(event.id) === `ghost-${String(dragInfo.id)}`);
            const eStart = isDragging ? dragInfo.start : new Date(event.start_datetime);
            const eEnd = isDragging ? dragInfo.end : new Date(event.end_datetime);

            let targetCol = -1;
            for (let col = 0; col < 10; col++) {
                const lastInCol = columns[col];
                const isLaneFree = !lastInCol || new Date(lastInCol) <= eStart;

                let isBlockedByDrag = false;
                if (dragInfo && !isDragging && Number.isFinite(dragInfo.lane) && dragInfo.lane === col) {
                    const dragOverlap = eStart < dragInfo.end && eEnd > dragInfo.start;
                    if (dragOverlap) isBlockedByDrag = true;
                }

                if (isLaneFree && !isBlockedByDrag) {
                    targetCol = col;
                    break;
                }
            }

            if (targetCol === -1) targetCol = 0;
            columns[targetCol] = eEnd;
            islandProcessed.push({ ...event, logicalLane: targetCol });
        });

        const usedLanes = [...new Set(islandProcessed.map(l => l.logicalLane))].sort((a, b) => a - b);
        const totalUsed = usedLanes.length || 1;
        const laneToRank = {};
        usedLanes.forEach((lane, idx) => {
            laneToRank[lane] = idx;
        });

        islandProcessed.forEach(l => {
            allLayouts.push({
                ...l,
                col: laneToRank[l.logicalLane],
                totalCols: totalUsed,
                savedLane: l.logicalLane
            });
        });
    });

    return allLayouts;
};
